import React, { useState, useEffect } from 'react';
import {
  Grid,
  PageHeader,
  CircularProgress,
  Box,
  AssuredWorkloadOutlinedIcon,
  Link,
  Tabs,
  Tab,
  Divider
} from '@esgian/esgianui';
import VoyageAnalyticsFilters from '@components/Sections/ShipAnalytics/VoyageAnalyticsSection/VoyageAnalyticsFilters';
import CompletedVoyageSection from '@components/Sections/VoyageAnalytics/CompletedVoyageSection';
import BulkSearchSection from '@components/Sections/VoyageAnalytics/BulkSearchSection';
import { useSelector } from 'react-redux';
import { getCompletedVoyagesFilters } from '@store/features/filters/VoyageAnalyticsPage/CompletedVoyage/CompletedVoyagesSlice';
import { getVoyageSearchDetails } from '@api';
import AccreditationStatementModal from '@components/Modals/AccreditationStatementModal';
import VoyageGroupsPopup from '@components/Sections/VoyageAnalytics/VoyageGroupSection/VoyageGroupsPopup';
import { useSegment } from '@hooks';
import moment from 'moment';
import { useVoyageGroups } from '../hooks/useVoyageGroups';
import { TextWithTooltipIcon } from '@components';
import { COMPLETED_VOYAGE_TITLE } from '@constants/tooltips';

function CompletedVoyagePage() {
  const [showStatement, setShowStatement] = useState(false);
  const [standardSearchResults, setStandardSearchResults] = useState([]);
  const [bulkSearchResults, setBulkSearchResults] = useState([]);
  const [bulkSearchData, setBulkSearchData] = useState([]);
  const [displayMode, setDisplayMode] = useState('table'); // store display mode preference
  const [loading, setLoading] = useState(false);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [init, setInit] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const reduxFilters = useSelector(getCompletedVoyagesFilters);
  const { segment } = useSegment();
  const { completedVoyagesGroups, prefetchTransitDetails } = useVoyageGroups();

  useEffect(() => {
    if (!completedVoyagesGroups || !completedVoyagesGroups.length) return;
    prefetchTransitDetails(
      completedVoyagesGroups.filter((g) => g.isFavorite && g.voyages.length > 0).map((g) => g.id)
    );
  }, [completedVoyagesGroups]);

  const checkValidFilters = (filters) => {
    return !!(
      filters &&
      filters.toId &&
      filters.fromId &&
      moment(filters.startDate).isValid() &&
      moment(filters.endDate).isValid()
    );
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchData = async () => {
      if (!checkValidFilters(reduxFilters)) {
        setStandardSearchResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        let body = {
          ...reduxFilters,
          toId: reduxFilters.toId.id ?? null,
          fromId: reduxFilters.fromId.id ?? null,
          segmentTypeId: segment.id
        };

        const response = await getVoyageSearchDetails(body, signal);
        const voyageSearchResults = response.voyageSearchResults || [];
        setStandardSearchResults(voyageSearchResults);
      } catch (error) {
        console.error('Error fetching voyage search results:', error);
        setStandardSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    if (shouldFetchData) {
      fetchData();
    }

    return () => {
      controller.abort();
    };
  }, [shouldFetchData, reduxFilters, segment.id]);

  useEffect(() => {
    if (!init && checkValidFilters(reduxFilters)) {
      setShouldFetchData(true);
      setInit(true);
    }
  }, [init, reduxFilters]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // When switching to standard search, fetch data if we have valid filters
    if (newValue === 0 && checkValidFilters(reduxFilters) && standardSearchResults.length === 0) {
      setShouldFetchData(true);
    }
  };

  const handleBulkSearchResults = (results) => {
    setBulkSearchResults(results);
  };

  const handleBulkSearchDataChange = (data) => {
    setBulkSearchData(data);
  };

  const handleDisplayModeChange = (mode) => {
    setDisplayMode(mode);
  };

  // Get the current results based on active tab
  const getCurrentResults = () => {
    return activeTab === 0 ? standardSearchResults : bulkSearchResults;
  };

  // Get appropriate title for results section
  const getResultsTitle = () => {
    const results = getCurrentResults();
    if (results.length === 0) {
      return 'Completed Voyages';
    }
    return `Completed Voyages (${activeTab === 0 ? 'Standard Search' : 'Bulk Search'} - ${
      results.length
    } results)`;
  };

  return (
    <Box sx={{ minHeight: '90vh', width: '100%' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <PageHeader
            id={'lbl-voyage-analytics'}
            title={'Voyage analytics'}
            icon={
              <AssuredWorkloadOutlinedIcon
                fontSize={'small'}
                onClick={() => {
                  setShowStatement(true);
                }}
                style={{ cursor: 'pointer' }}
              />
            }
          />
        </Grid>
        <Grid item container xs={6} spacing={1} justifyContent="flex-end" alignItems="center">
          <VoyageGroupsPopup mode="search" />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextWithTooltipIcon
              label={'Completed voyage'}
              tooltipText={COMPLETED_VOYAGE_TITLE}
              labelVariant={'body2'}
            />
            <Link
              href="https://www.youtube.com/watch?v=bs_r0aF94ak"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ marginLeft: 1, fontSize: '14px' }}>
              (Introduction Video)
            </Link>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Standard Search" />
            <Tab label="Bulk Search" />
          </Tabs>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        {activeTab === 0 && (
          <>
            <VoyageAnalyticsFilters
              checkValidFilters={checkValidFilters}
              handleApplyFilters={() => {
                setShouldFetchData(true);
              }}
            />

            {loading && (
              <Grid
                item
                xs={12}
                style={{
                  minHeight: '400px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative'
                }}>
                <CircularProgress
                  sx={{
                    position: 'absolute',
                    top: 'calc(50% - 50px)',
                    left: 'calc(50% - 50px)'
                  }}
                  size={100}
                />
              </Grid>
            )}
          </>
        )}

        {activeTab === 1 && (
          <Grid item xs={12}>
            <BulkSearchSection
              onSearchResults={handleBulkSearchResults}
              voyageData={bulkSearchData}
              onDataChange={handleBulkSearchDataChange}
              displayMode={displayMode}
              onDisplayModeChange={handleDisplayModeChange}
            />
          </Grid>
        )}

        {/* Shared results section for both search types */}
        {!loading && getCurrentResults().length > 0 && (
          <Grid
            item
            xs={12}
            style={{
              minHeight: '400px',
              marginTop: 16
            }}>
            <CompletedVoyageSection
              loading={loading}
              completedVoyagesData={getCurrentResults()}
              title={getResultsTitle()}
            />
          </Grid>
        )}

        <AccreditationStatementModal
          handleClose={() => setShowStatement(false)}
          open={showStatement}
        />
      </Grid>
    </Box>
  );
}

export default CompletedVoyagePage;
